/**
 * DuplicateActionDialog — Merge or Delete duplicate records.
 * Extracted from DuplicateDetectionPage.js to reduce component size.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Label } from '../../components/ui/label';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { Loader2, GitMerge, Trash2 } from 'lucide-react';

export function DuplicateActionDialog({
  actionDialog, onClose,
  selectedKeep, setSelectedKeep,
  selectedDelete, setSelectedDelete,
  actionLoading,
  onMerge, onDelete,
}) {
  return (
    <Dialog open={!!actionDialog} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-md" data-testid="action-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {actionDialog?.action === 'merge'
              ? <GitMerge className="h-5 w-5 text-primary" />
              : <Trash2 className="h-5 w-5 text-destructive" />
            }
            {actionDialog?.action === 'merge' ? 'Merge Duplicates' : 'Delete Duplicates'}
          </DialogTitle>
          <DialogDescription>
            {actionDialog?.action === 'merge'
              ? 'Select the record to keep. All stock quantities and references will be merged into it. The others will be deleted.'
              : 'Select items to delete. At least one must be kept.'}
          </DialogDescription>
        </DialogHeader>

        {actionDialog?.action === 'merge' && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Keep this record:</Label>
            <RadioGroup value={selectedKeep} onValueChange={setSelectedKeep}>
              {actionDialog.items.map((item) => {
                const id = actionDialog.type === 'product' ? item.product_id : item.patient_id;
                const name = item.name || item.product_name || id;
                return (
                  <label key={id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${selectedKeep === id ? 'bg-primary/5 border-primary/40' : 'hover:bg-muted/50'}`}>
                    <RadioGroupItem value={id} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">
                        {actionDialog.type === 'product'
                          ? `${item.generic_name || ''} | ${item.manufacturer || ''} | Stock: ${item.stock_quantity ?? '?'}`
                          : `${item.phone || ''} ${item.email || ''}`}
                      </p>
                      <p className="text-[9px] text-muted-foreground font-mono">{id}</p>
                    </div>
                    {selectedKeep === id && <Badge className="text-[9px] bg-primary/10 text-primary border-primary/30 shrink-0" variant="outline">Keep</Badge>}
                  </label>
                );
              })}
            </RadioGroup>
            {actionDialog.type === 'product' && (
              <p className="text-[10px] text-muted-foreground mt-1">Stock from deleted items will be added to the kept product. Batches will be combined.</p>
            )}
          </div>
        )}

        {actionDialog?.action === 'delete' && (
          <div className="space-y-2">
            <Label className="text-xs font-semibold">Select items to delete:</Label>
            {actionDialog.items.map((item) => {
              const id = actionDialog.type === 'product' ? item.product_id : item.patient_id;
              const name = item.name || item.product_name || id;
              const checked = selectedDelete[id] || false;
              return (
                <label key={id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${checked ? 'bg-red-500/5 border-red-500/30' : 'hover:bg-muted/50'}`}>
                  <input type="checkbox" checked={checked}
                    onChange={() => setSelectedDelete(prev => ({ ...prev, [id]: !prev[id] }))}
                    className="h-4 w-4 accent-red-500 rounded" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium">{name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">
                      {actionDialog.type === 'product'
                        ? `${item.generic_name || ''} | Stock: ${item.stock_quantity ?? '?'}`
                        : `${item.phone || ''} ${item.email || ''}`}
                    </p>
                    <p className="text-[9px] text-muted-foreground font-mono">{id}</p>
                  </div>
                  {checked && <Badge className="text-[9px] bg-red-500/10 text-red-600 border-red-500/30 shrink-0" variant="outline">Delete</Badge>}
                </label>
              );
            })}
            <p className="text-[10px] text-destructive mt-1">Deleted items cannot be recovered. Sales history referencing deleted items will remain.</p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {actionDialog?.action === 'merge' ? (
            <Button onClick={onMerge} disabled={actionLoading || !selectedKeep} className="gap-1.5" data-testid="confirm-merge-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <GitMerge className="h-4 w-4" />}
              Merge & Keep Selected
            </Button>
          ) : (
            <Button variant="destructive" onClick={onDelete}
              disabled={actionLoading || !Object.values(selectedDelete).some(Boolean)} className="gap-1.5" data-testid="confirm-delete-btn">
              {actionLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete Selected
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
